import { useState } from 'react';
import { ChevronDown, ChevronRight, Shield } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils/cn';
import { CodeSnippet } from './code-snippet';
import type { ApiEndpoint } from '../data/api-endpoints';

const methodColors: Record<string, string> = {
  GET: 'bg-green-500/15 text-green-700 border-green-500/30',
  POST: 'bg-blue-500/15 text-blue-700 border-blue-500/30',
  PUT: 'bg-yellow-500/15 text-yellow-700 border-yellow-500/30',
  DELETE: 'bg-red-500/15 text-red-700 border-red-500/30',
};

interface EndpointCardProps {
  endpoint: ApiEndpoint;
}

export function EndpointCard({ endpoint }: EndpointCardProps) {
  const [expanded, setExpanded] = useState(false);

  return (
    <Card className="overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-muted/50 transition-colors"
      >
        {expanded ? (
          <ChevronDown className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
        )}
        <span
          className={cn(
            'inline-flex items-center justify-center px-2.5 py-0.5 rounded text-xs font-bold border min-w-[60px]',
            methodColors[endpoint.method]
          )}
        >
          {endpoint.method}
        </span>
        <code className="text-sm font-mono text-muted-foreground">{endpoint.path}</code>
        <span className="text-sm font-medium ml-2">{endpoint.title}</span>
        {endpoint.permission && (
          <Badge variant="outline" className="ml-auto text-xs gap-1">
            <Shield className="w-3 h-3" />
            {endpoint.permission}
          </Badge>
        )}
      </button>

      {expanded && (
        <div className="border-t border-border p-4 space-y-4 bg-muted/20">
          <p className="text-sm text-muted-foreground">{endpoint.description}</p>

          {endpoint.pathParams && endpoint.pathParams.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold mb-2">Parámetros de ruta</h4>
              <div className="rounded border border-border overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted/50">
                      <th className="text-left px-3 py-2 font-medium">Nombre</th>
                      <th className="text-left px-3 py-2 font-medium">Tipo</th>
                      <th className="text-left px-3 py-2 font-medium">Descripción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {endpoint.pathParams.map((param) => (
                      <tr key={param.name} className="border-t border-border">
                        <td className="px-3 py-2 font-mono text-xs">{param.name}</td>
                        <td className="px-3 py-2">
                          <Badge variant="secondary" className="text-xs">
                            {param.type}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">{param.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {endpoint.requestBody && (
            <div>
              <h4 className="text-sm font-semibold mb-2">Request Body</h4>
              <pre className="p-3 rounded bg-zinc-950 text-zinc-100 text-xs overflow-x-auto leading-relaxed">
                <code>{endpoint.requestBody}</code>
              </pre>
            </div>
          )}

          {endpoint.responseExample && (
            <div>
              <h4 className="text-sm font-semibold mb-2">Response Example</h4>
              <pre className="p-3 rounded bg-zinc-950 text-zinc-100 text-xs overflow-x-auto leading-relaxed">
                <code>{endpoint.responseExample}</code>
              </pre>
            </div>
          )}

          <div>
            <h4 className="text-sm font-semibold mb-2">Snippets de código</h4>
            <CodeSnippet
              method={endpoint.method}
              path={endpoint.path}
              requestBody={endpoint.requestBody}
            />
          </div>
        </div>
      )}
    </Card>
  );
}
